import {
	Dialog,
	DialogBody,
	DialogContent,
	DialogTitle,
	Radio,
} from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import { DialogActions } from "molecules/dialog/DialogActions";
import { IconCopy, IconExternalLink } from "@tabler/icons-react";
import { type ReactNode, useId } from "react";
import { useTranslation } from "react-i18next";

type AuthMode = "redirect" | "copy";

export interface OAuthAuthorizationViewProps {
	isOpen: boolean;
	onClose: () => void;
	title: string;
	description?: string | undefined;
	providerName: string;
	showModeSelection?: boolean;
	mode: AuthMode;
	onModeChange: (mode: AuthMode) => void;
	isLoading: boolean;
	onSubmit: () => void;
	submitDisabled?: boolean;
	children?: ReactNode;
}

export const OAuthAuthorizationView = ({
	isOpen,
	onClose,
	title,
	description,
	providerName,
	showModeSelection = false,
	mode,
	onModeChange,
	isLoading,
	onSubmit,
	submitDisabled = false,
	children,
}: OAuthAuthorizationViewProps) => {
	const { t } = useTranslation();
	const radioGroup = useId();

	return (
		<Dialog open={isOpen} onOpenChange={onClose}>
			<DialogContent size="md">
				<DialogTitle>{title}</DialogTitle>
				<DialogBody>
					{description && (
						<div className="mt-2 mb-4 text-sm text-gray-700">{description}</div>
					)}
					{showModeSelection && (
						<>
							<label className="mb-5 flex items-center gap-2">
								<div className="shrink-0">
									<Radio
										name={radioGroup}
										checked={mode === "redirect"}
										onChange={(event) => {
											if (event.target.checked) {
												onModeChange("redirect");
											}
										}}
									/>
								</div>
								<div>
									<div className="text-sm font-medium text-gray-700">
										{t("I want to do it myself")}
									</div>
									<div className="text-sm text-gray-500">
										{t(
											"You'll be redirected to {{provider}} to sign in and approve the integration",
											{ provider: providerName },
										)}
									</div>
								</div>
							</label>
							<label className="mb-6 flex items-center gap-2">
								<div className="shrink-0">
									<Radio
										name={radioGroup}
										checked={mode === "copy"}
										onChange={(event) => {
											if (event.target.checked) {
												onModeChange("copy");
											}
										}}
									/>
								</div>
								<div>
									<div className="text-sm font-medium text-gray-700">
										{t("I want to share a link with a non-Exopen user")}
									</div>
									<div className="text-sm text-gray-500">
										{t(
											"An external user will use the link to sign in and approve the integration. Copy the link and send it to a user with a {{provider}} account.",
											{ provider: providerName },
										)}
									</div>
								</div>
							</label>
						</>
					)}
					{children}
				</DialogBody>
				<DialogActions withCancelButton>
					<Button
						variant="primary"
						isLoading={isLoading}
						disabled={submitDisabled}
						onClick={onSubmit}
						icon={mode === "redirect" ? <IconExternalLink /> : <IconCopy />}
						iconPosition={mode === "redirect" ? "trailing" : undefined}
					>
						{mode === "redirect" ? t("Continue") : t("Copy Link")}
					</Button>
				</DialogActions>
			</DialogContent>
		</Dialog>
	);
};
